import { showToast } from '@quickmd-llc/design-system'
import {
  getGetPatientsPatientIdMediafilesFileIdQueryKey,
  getPatientsPatientIdMediafilesFileId,
} from 'api/generated/media-file/media-file'
import { client } from 'api/components/queryClient'

export const DONT_SHOW_COMPLETE_CONFIRMATION_KEY = 'qmd_provider_skip_complete_visit_confirmation'
export const DONT_SHOW_DEFER_CONFIRMATION_KEY = 'qmd_provider_skip_defer_visit_confirmation'

export const removeAllTags = (str: string) => {
  if (!str) return str
  // Only strip complete <...> tags. The previous pattern /<\/?[^>]+(>|$)/g
  // had a `$` alternative that matched from a bare `<` to end-of-string, so a
  // legacy plain-text note containing `<` (e.g. "BP < 120, follow up") got
  // truncated at the `<` when copied. Quill HTML notes encode `<` as `&lt;`,
  // which is why this only surfaced for pre-Quill plain-text notes.
  return str.replace(/<[^>]*>/g, '')
}

// Re-export for backward compatibility with existing imports from this file.
export { sanitizeNoteHtml } from '../../../../utils/noteHtmlSanitizer'

// Block-level tags whose boundaries must become line breaks in plain text.
// Rich-text (Quill) notes store paragraphs as block elements with no literal
// newlines between them; without this, textContent would run every paragraph
// together (QMD-3817).
const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'UL', 'OL', 'BLOCKQUOTE', 'PRE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'])

// Walk the parsed DOM, collecting text and inserting a newline after each
// block element and for every <br>. Reading text off text nodes still decodes
// entities, so legacy plain-text notes keep literal `<`/`>` operators. Legacy
// notes contain no real block tags (sanitizeNoteHtml escapes them), so this is
// a no-op for them and their existing literal newlines pass through untouched.
const collectText = (node: Node, parts: string[]): void => {
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      parts.push(child.textContent ?? '')
      return
    }
    if (child instanceof HTMLElement) {
      if (child.tagName === 'BR') {
        parts.push('\n')
        return
      }
      collectText(child, parts)
      if (BLOCK_TAGS.has(child.tagName)) {
        parts.push('\n')
      }
    }
  })
}

// Convert (already-sanitized) note HTML to faithful plain text for the
// clipboard `text/plain` payload. Preserves paragraph/list structure from
// rich-text notes while keeping legacy plain-text notes byte-identical.
export const htmlToPlainText = (html: string): string => {
  if (!html) return html
  const el = document.createElement('div')
  el.innerHTML = html
  const parts: string[] = []
  collectText(el, parts)
  // trimEnd drops the trailing newline the last block element would otherwise
  // add, so single-paragraph notes match their pre-fix output exactly.
  return parts.join('').trimEnd()
}

export function fetchPatientMediaFile(
  patientId: string,
  fileId: string | undefined,
): Promise<Awaited<ReturnType<typeof getPatientsPatientIdMediafilesFileId>> | undefined> {
  if (!patientId || !fileId) {
    showToast({ type: 'error', message: 'Failed to load drug screen file.' })
    return Promise.resolve(undefined)
  }

  return getPatientsPatientIdMediafilesFileId(patientId, fileId)
    .then((response) => {
      const queryKey = getGetPatientsPatientIdMediafilesFileIdQueryKey(patientId, fileId)
      client.setQueryData(queryKey, response)
      return response
    })
    .catch(() => {
      showToast({ type: 'error', message: 'Failed to load drug screen file.' })
      return undefined
    })
}

// Cookie-based persistence that survives localStorage.clear() calls.
function setCookie(name: string, value: string, days = 3650) {
  const date = new Date()
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
  const expires = `expires=${date.toUTCString()}`
  document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/`
}

function getCookie(name: string): string | null {
  const nameEQ = `${name}=`
  const parts = document.cookie.split(';')
  for (let i = 0; i < parts.length; i += 1) {
    const c = (parts[i] ?? '').trim()
    if (c.startsWith(nameEQ)) return decodeURIComponent(c.substring(nameEQ.length))
  }
  return null
}

export function setDontShowCompleteConfirmation(enabled: boolean) {
  setCookie(DONT_SHOW_COMPLETE_CONFIRMATION_KEY, enabled ? '1' : '0')
}

export function getDontShowCompleteConfirmation(): boolean {
  const v = getCookie(DONT_SHOW_COMPLETE_CONFIRMATION_KEY)
  return v === '1'
}

export function setDontShowDeferConfirmation(enabled: boolean) {
  setCookie(DONT_SHOW_DEFER_CONFIRMATION_KEY, enabled ? '1' : '0')
}

export function getDontShowDeferConfirmation(): boolean {
  const v = getCookie(DONT_SHOW_DEFER_CONFIRMATION_KEY)
  return v === '1'
}
